import React, { useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";
import {
  ApiBase_slashless,
  createVideoGalleryCopyFrom,
  deleteVideoGallery,
  loadVideoGalleries,
  replaceVideoGalleryFileFrom,
  saveVideoGallery,
} from "../utils/Network";

function videoFileUrl(path) {
  if (path == null || path === "") return null;
  if (/^https?:\/\//i.test(String(path))) return path;
  const base = String(ApiBase_slashless).replace(/\/$/, "");
  const p = String(path);
  return p.startsWith("/") ? `${base}${p}` : `${base}/${p}`;
}

/**
 * Modal to pick a video from the CMS video gallery or upload a new one (same UX pattern as {@link Gallery}).
 */
export default function VideoGalleryPickerModal({
  isOpen,
  closeGallery,
  languageId,
  onVideoSelected,
  /** When set, picker replaces that gallery row’s file (no new row). Uses `replaceMeta` for PATCH fields. */
  replaceVideoId = null,
  replaceMeta = null,
  onReplaceComplete = null,
  /** List page “add”: duplicate an existing row or upload; no talent-document callback. */
  listAddMode = false,
  onListAdded = null,
}) {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [error, setError] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(null);
  const [pages, setPages] = useState([]);
  const [page, setPage] = useState(1);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [activeTab, setActiveTab] = useState("videos");
  const [deleteLoading, setDeleteLoading] = useState(null);
  const inputFileRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setPickerOpen(true);
      setActiveTab("videos");
      setPage(1);
      getData(1);
    } else {
      setPickerOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (filePreview) URL.revokeObjectURL(filePreview);
    };
  }, [filePreview]);

  const getData = (pageNum) => {
    setLoading(true);
    loadVideoGalleries("", pageNum).then((data) => {
      const results = data.results ?? (Array.isArray(data) ? data : []);
      setItems(results);
      if (data.count != null) {
        setCount(data.count);
        const pageCount =
          data.count % 12 === 0
            ? parseInt(data.count / 12, 10)
            : parseInt(data.count / 12, 10) + 1;
        setPages(range(1, pageCount));
      } else {
        setCount(results.length);
        setPages([1]);
      }
      setLoading(false);
    });
  };

  function range(start, end) {
    return Array.apply(0, Array(end)).map((_, index) => index + start);
  }

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) {
      setFile(f);
      if (filePreview) URL.revokeObjectURL(filePreview);
      setFilePreview(URL.createObjectURL(f));
      if (!title) {
        setTitle(f.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  const clearSelectedFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    setFile(null);
    if (inputFileRef.current) inputFileRef.current.value = "";
  };

  const uploadToGallery = () => {
    setError(false);
    if (!file) {
      setError("Please select a video file");
      return;
    }

    if (replaceVideoId && replaceMeta) {
      setLoading(true);
      saveVideoGallery(
        replaceVideoId,
        replaceMeta.title,
        replaceMeta.language,
        replaceMeta.image || "",
        replaceMeta.tag || "",
        file
      )
        .then((data) => {
          setLoading(false);
          if (data && data.id) {
            setTitle("");
            setFile(null);
            if (filePreview) URL.revokeObjectURL(filePreview);
            setFilePreview(null);
            if (inputFileRef.current) inputFileRef.current.value = "";
            if (onReplaceComplete) onReplaceComplete(data);
            closeModal();
          } else if (data && data.detail) {
            setError(String(data.detail));
          } else {
            setError("Update failed");
          }
        })
        .catch((e) => {
          setLoading(false);
          setError(e.message || "Update failed");
        });
      return;
    }

    if (!title) {
      setError("Please enter a name");
      return;
    }
    const effectiveLanguageId = listAddMode ? languageId || 3 : languageId;
    if (!effectiveLanguageId) {
      setError("Please select a language tab first");
      return;
    }
    setLoading(true);
    saveVideoGallery(null, title, effectiveLanguageId, "", "", file)
      .then((data) => {
        setLoading(false);
        if (data && data.file) {
          setTitle("");
          setFile(null);
          if (filePreview) URL.revokeObjectURL(filePreview);
          setFilePreview(null);
          if (inputFileRef.current) inputFileRef.current.value = "";
          setActiveTab("videos");
          getData(1);
          if (listAddMode && onListAdded && data.id) {
            onListAdded(data);
          } else if (onVideoSelected) {
            onVideoSelected(data.file);
          }
          closeModal();
        } else if (data && data.detail) {
          setError(String(data.detail));
        } else {
          setError("Upload failed");
        }
      })
      .catch((e) => {
        setLoading(false);
        setError(e.message || "Upload failed");
      });
  };

  const closeModal = () => {
    setPickerOpen(false);
    closeGallery();
  };

  const loadPage = (e, pageNum) => {
    e.preventDefault();
    setPage(pageNum);
    getData(pageNum);
  };

  const selectItem = async (item) => {
    if (!item?.file) return;

    if (replaceVideoId && replaceMeta) {
      setError(false);
      setLoading(true);
      try {
        const data = await replaceVideoGalleryFileFrom(replaceVideoId, item.id);
        if (data && data.id) {
          if (onReplaceComplete) onReplaceComplete(data);
          closeModal();
        } else {
          setError("Update failed");
        }
      } catch (e) {
        setError(e.message || "Could not replace with that video");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (listAddMode) {
      setError(false);
      setLoading(true);
      const extra = languageId ? { language: languageId } : {};
      createVideoGalleryCopyFrom(item.id, extra)
        .then((data) => {
          if (onListAdded && data && data.id) {
            onListAdded(data);
          }
          getData(1);
          closeModal();
        })
        .catch((e) => {
          setError(e.message || "Could not add from gallery");
        })
        .finally(() => setLoading(false));
      return;
    }

    if (onVideoSelected) onVideoSelected(item.file);
    closeModal();
  };

  const deleteGalleryItem = (e, id) => {
    e.preventDefault();
    if (replaceVideoId && id === replaceVideoId) return;
    setDeleteLoading(id);
    deleteVideoGallery(id)
      .then(() => getData(page))
      .finally(() => setDeleteLoading(null));
  };

  return (
    <ReactModal
      closeTimeoutMS={500}
      isOpen={pickerOpen}
      onRequestClose={closeModal}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      className="Modal Modal--cms-gallery"
      ariaHideApp={false}
      overlayClassName="Overlay Overlay--cms-gallery"
      style={{
        overlay: { zIndex: 2000 },
        content: {
          zIndex: 2001,
          width: "95%",
          maxWidth: "1400px",
          inset: "50% auto auto 50%",
          transform: "translate(-50%, -50%)",
        },
      }}
    >
      <div className="modal-content cms-gallery-modal-content">
        <div className="modal-body gallery padding-0 cms-gallery-modal-body">
            <div className="cms-gallery-shell">
            <div className="cms-gallery-topbar">
              {listAddMode ? (
                <p className="cms-gallery-replace-hint text-muted margin-b-10 margin-t-5">
                  Pick a video to duplicate into the gallery, or use Upload to add a new file.
                </p>
              ) : replaceVideoId ? (
                <p className="cms-gallery-replace-hint text-muted margin-b-10 margin-t-5">
                  Choose another gallery video or upload a new file to replace this item.
                </p>
              ) : null}
              <div className="cms-gallery-tabs" role="tablist" aria-label="Video gallery tabs">
                <button
                  type="button"
                  className={`cms-gallery-tab-btn ${activeTab === "videos" ? "active" : ""}`}
                  onClick={() => setActiveTab("videos")}
                >
                  <i className="fa fa-th-large margin-r-5"></i>
                  Select video
                </button>
                <button
                  type="button"
                  className={`cms-gallery-tab-btn ${activeTab === "upload" ? "active" : ""}`}
                  onClick={() => setActiveTab("upload")}
                >
                  <i className="fa fa-upload margin-r-5"></i>
                  Upload
                </button>
              </div>
            </div>

            <div className="cms-gallery-tab-content">
              <div className={`tab-pane ${activeTab === "videos" ? "active" : ""}`} id="videos">
                {loading && (
                  <div className="gallery-loading">Please wait…</div>
                )}
                <div className="gallery-tab cms-gallery-grid">
                  {items &&
                    items
                      .filter((item) =>
                        replaceVideoId ? item.id !== replaceVideoId : true
                      )
                      .map((item) => (
                      <div
                        key={item.id}
                        className={`gallery-item cms-gallery-item ${
                          deleteLoading === item.id ? "gallery_delete_loading" : ""
                        }`}
                      >
                        <button
                          type="button"
                          className="cms-gallery-item-open"
                          onClick={() => selectItem(item)}
                        >
                          {item.image ? (
                            <img src={item.image} alt={item.title || "video"} />
                          ) : (
                            <video
                              className="cms-gallery-video-thumb"
                              preload="metadata"
                              muted
                              playsInline
                            >
                              <source src={videoFileUrl(item.file)} />
                            </video>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn-gallery-delete"
                          onClick={(e) => {
                            if (window.confirm("Delete this gallery video?")) {
                              deleteGalleryItem(e, item.id);
                            }
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                        <div className="cms-gallery-item-meta">
                          <span>{item.title || `Video #${item.id}`}</span>
                        </div>
                      </div>
                    ))}
                  <div className="clear"></div>
                </div>

                <ul className="pagination cms-pagination m-0 float-right">
                  {pages &&
                    pages.map((pageNumber) => (
                      <li key={pageNumber} className="page-item">
                        <a
                          className={
                            pageNumber === page ? "page-link active" : "page-link"
                          }
                          href="#loadpage"
                          onClick={(e) => loadPage(e, pageNumber)}
                        >
                          {pageNumber}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>

              <div className={`tab-pane ${activeTab === "upload" ? "active" : ""}`} id="upload">
                <div className="gallery-upload cms-gallery-upload-panel">
                  <div className="cms-gallery-upload-stack">
                    <label className="cms-gallery-dropzone">
                      <input
                        type="file"
                        ref={inputFileRef}
                        accept="video/*,.mp4,.webm,.mov,.m4v"
                        onChange={handleFileChange}
                        className="hide"
                      />
                      {filePreview ? (
                        <>
                          <video
                            src={filePreview}
                            className="cms-gallery-dropzone-preview"
                            controls
                            muted
                          />
                          <button
                            type="button"
                            className="cms-gallery-dropzone-clear"
                            onClick={clearSelectedFile}
                            title="Remove selected file"
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <i className="fa fa-cloud-upload"></i>
                          <span>Click to choose a video file</span>
                          <small>MP4, WebM, MOV, …</small>
                        </>
                      )}
                    </label>
                    {!replaceVideoId ? (
                      <input
                        type="text"
                        value={title}
                        placeholder="Video title"
                        onChange={(e) => setTitle(e.target.value)}
                        className="form-control cms-gallery-input"
                      />
                    ) : null}
                    <button type="button" className="btn btn-cm" onClick={uploadToGallery}>
                      <i className="fa fa-cloud-upload margin-r-5"></i>
                      {replaceVideoId ? "Replace with this file" : "Upload to gallery & use"}
                    </button>
                  </div>
                </div>
                {error && <div className="error">{error}</div>}
                {loading && (
                  <div className="gallery-loading">Please wait…</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ReactModal>
  );
}
